// Web Locks API gives us a per-origin mutex shared across tabs, workers, and
// service workers. We use it to serialize JWT refreshes so concurrent tabs
// don't race against SuperTokens' one-time refresh-token rotation.

const DEFAULT_LOCK_TIMEOUT_MS = 10_000;

export const JWT_REFRESH_LOCK_NAME = 'eluve:jwt-refresh';

const isWebLocksSupported = (): boolean =>
  typeof navigator !== 'undefined' && Boolean(navigator.locks);

export const withRefreshLock = async <T>(
  name: string,
  fn: () => Promise<T>,
  timeoutMs: number = DEFAULT_LOCK_TIMEOUT_MS,
): Promise<T> => {
  if (!isWebLocksSupported()) {
    return fn();
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  // Track whether we ever held the lock. Without this, an fn() that throws
  // after the timer fires (e.g. its own internal timeout at t > timeoutMs)
  // would trip the aborted-fallback path and run fn() a second time even
  // though the lock was granted and the failure was fn's own — not a stuck
  // lock holder.
  let lockGranted = false;

  try {
    return await navigator.locks.request(
      name,
      { signal: controller.signal },
      async () => {
        lockGranted = true;
        return fn();
      },
    );
  } catch (err) {
    // Only fall through when the abort fired before the lock was granted —
    // that's the genuine "stuck holder" case worth racing past.
    if (controller.signal.aborted && !lockGranted) {
      return fn();
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};
